
import React, { useState, useEffect } from 'react'
import {DollarSign, TrendingUp, TrendingDown, CreditCard, Wallet, BarChart3, PieChart, Calendar, FileText, Filter, Download, Plus, Search, Clock, AlertTriangle, CheckCircle, XCircle, Building, Target, Banknote, Edit, Trash2} from 'lucide-react'
import { useFinanceiro } from '../hooks/useFinanceiro'
import FinanceiroMetricsCard from '../components/FinanceiroMetricsCard'
import FinanceiroCharts from '../components/FinanceiroCharts'
import MovimentacaoBancariaForm from '../components/MovimentacaoBancariaForm'
import { lumi } from '../lib/lumi'
import { 
  gerarRelatorioDRE, 
  gerarRelatorioMargemObra, 
  gerarRelatorioPerformance,
  downloadPDF 
} from '../utils/financeiroReportUtils'
import toast from 'react-hot-toast'

type AbaAtiva = 'dashboard' | 'receitas' | 'despesas' | 'fluxo' | 'bancario' | 'relatorios'

export const Financeiro: React.FC = () => {
  const {
    contasReceber,
    contasPagar,
    fluxoCaixa,
    movimentacoesBancarias,
    dadosGraficos,
    metricas,
    loading,
    carregarDados,
    atualizarStatusContaReceber,
    atualizarStatusContaPagar
  } = useFinanceiro()

  const [abaAtiva, setAbaAtiva] = useState<AbaAtiva>('dashboard')
  const [filtroData, setFiltroData] = useState('')
  const [filtroObra, setFiltroObra] = useState('')
  const [filtroStatus, setFiltroStatus] = useState('')
  const [busca, setBusca] = useState('')
  
  // Estados para movimentação bancária
  const [contasBancarias, setContasBancarias] = useState<any[]>([])
  const [showMovimentacaoForm, setShowMovimentacaoForm] = useState(false)
  const [movimentacaoEditando, setMovimentacaoEditando] = useState<any>(null)

  // Estados para relatórios
  const [obras, setObras] = useState<any[]>([])
  const [filtroRelatorio, setFiltroRelatorio] = useState({
    periodo: 'ultimo_mes',
    obra: '',
    cliente: '',
    dataInicio: '',
    dataFim: ''
  })
  const [loadingRelatorio, setLoadingRelatorio] = useState(false)

  useEffect(() => {
    carregarDados()
    carregarContasBancarias()
    carregarObras()
  }, [carregarDados])

  const carregarContasBancarias = async () => {
    try {
      const { list } = await lumi.entities.contas_bancarias.list()
      setContasBancarias(list || [])
    } catch (error) {
      console.error('Erro ao carregar contas bancárias:', error)
    }
  }

  const carregarObras = async () => {
    try {
      const { list } = await lumi.entities.planejamento_obras.list()
      setObras(list || [])
    } catch (error) {
      console.error('Erro ao carregar obras:', error)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pendente':
        return <Clock size={16} className="text-yellow-600" />
      case 'vencido':
        return <AlertTriangle size={16} className="text-red-600" />
      case 'recebido':
      case 'pago':
      case 'concluido':
        return <CheckCircle size={16} className="text-green-600" />
      case 'cancelado':
        return <XCircle size={16} className="text-gray-600" />
      default:
        return <Clock size={16} className="text-gray-600" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente':
      case 'processando':
        return 'bg-yellow-100 text-yellow-800'
      case 'vencido':
        return 'bg-red-100 text-red-800'
      case 'recebido':
      case 'pago':
      case 'concluido':
        return 'bg-green-100 text-green-800'
      case 'cancelado':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const exportarRelatorio = async (tipo: string) => {
    setLoadingRelatorio(true)
    try {
      let doc
      const filename = `${tipo.toLowerCase().replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`
      
      switch (tipo) {
        case 'DRE':
          doc = await gerarRelatorioDRE(movimentacoesBancarias, getPeriodoTexto())
          break
        case 'Margem por Obra':
          doc = await gerarRelatorioMargemObra(movimentacoesBancarias, obras)
          break
        case 'Performance Mensal':
          doc = await gerarRelatorioPerformance(movimentacoesBancarias, getPeriodoTexto())
          break
        default:
          toast.error('Tipo de relatório não reconhecido')
          return
      }
      
      if (doc) {
        downloadPDF(doc, filename)
        toast.success(`Relatório ${tipo} exportado com sucesso!`)
      }
    } catch (error) {
      console.error(`Erro ao gerar relatório ${tipo}:`, error)
      toast.error(`Erro ao gerar relatório ${tipo}`)
    } finally {
      setLoadingRelatorio(false)
    }
  }

  const getPeriodoTexto = () => {
    switch (filtroRelatorio.periodo) {
      case 'ultimo_mes':
        return 'Último mês'
      case 'ultimos_3_meses':
        return 'Últimos 3 meses'
      case 'ultimos_6_meses':
        return 'Últimos 6 meses'
      case 'ultimo_ano':
        return 'Último ano'
      case 'personalizado':
        return `${filtroRelatorio.dataInicio} a ${filtroRelatorio.dataFim}`
      default:
        return 'Período não especificado'
    }
  }

  const handleEditarMovimentacao = (movimentacao: any) => {
    setMovimentacaoEditando(movimentacao)
    setShowMovimentacaoForm(true)
  }

  const handleExcluirMovimentacao = async (movimentacaoId: string) => {
    if (confirm('Tem certeza que deseja excluir esta movimentação?')) {
      try {
        await lumi.entities.movimentacoes_bancarias.delete(movimentacaoId)
        await carregarDados()
        toast.success('Movimentação excluída com sucesso!')
      } catch (error) {
        console.error('Erro ao excluir movimentação:', error)
        toast.error('Erro ao excluir movimentação')
      }
    }
  }

  const handleMovimentacaoSuccess = () => {
    carregarDados()
    setMovimentacaoEditando(null)
  }

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <FinanceiroMetricsCard
          title="Receitas do Mês"
          value={metricas.receitasMes}
          previousValue={metricas.comparativoMesAnterior.receitas}
          icon={<TrendingUp size={24} />}
          color="green"
        />
        <FinanceiroMetricsCard
          title="Despesas do Mês"
          value={metricas.despesasMes}
          previousValue={metricas.comparativoMesAnterior.despesas}
          icon={<TrendingDown size={24} />}
          color="red"
        />
        <FinanceiroMetricsCard
          title="Saldo Atual"
          value={metricas.saldoAtual}
          previousValue={metricas.comparativoMesAnterior.saldo}
          icon={<Wallet size={24} />}
          color="blue"
        />
        <FinanceiroMetricsCard
          title="Contas a Receber"
          value={metricas.contasReceber}
          icon={<CreditCard size={24} />}
          color="indigo"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <FinanceiroMetricsCard
          title="Contas a Pagar"
          value={metricas.contasPagar}
          icon={<DollarSign size={24} />}
          color="yellow"
        />
        <FinanceiroMetricsCard
          title="Fluxo de Caixa"
          value={metricas.fluxoCaixa}
          icon={<BarChart3 size={24} />}
          color="purple"
        />
        <FinanceiroMetricsCard
          title="Margem de Lucro"
          value={metricas.margemLucro}
          format="percentage"
          icon={<PieChart size={24} />}
          color="green"
        />
        <FinanceiroMetricsCard
          title="Total em Obras"
          value={metricas.totalObras}
          icon={<FileText size={24} />}
          color="indigo"
        />
      </div>

      {/* Gráficos com dados reais */}
      <FinanceiroCharts data={dadosGraficos} />

      {/* Alertas e Resumos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contas Vencidas */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-red-600">Contas Vencidas</h3>
          <div className="space-y-3">
            {[...contasReceber, ...contasPagar]
              .filter(conta => conta.status === 'vencido')
              .slice(0, 5)
              .map((conta, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">
                      {'cliente' in conta ? conta.cliente : conta.fornecedor}
                    </p>
                    <p className="text-xs text-gray-600">
                      Vencimento: {formatDate(conta.vencimento)}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-red-600">
                    {formatCurrency(conta.valor)}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Próximos Vencimentos */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 text-yellow-600">Próximos Vencimentos</h3>
          <div className="space-y-3">
            {[...contasReceber, ...contasPagar]
              .filter(conta => {
                const vencimento = new Date(conta.vencimento)
                const hoje = new Date()
                const proximosDias = new Date()
                proximosDias.setDate(hoje.getDate() + 7)
                return conta.status === 'pendente' && vencimento <= proximosDias && vencimento >= hoje
              })
              .slice(0, 5)
              .map((conta, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium">
                      {'cliente' in conta ? conta.cliente : conta.fornecedor}
                    </p>
                    <p className="text-xs text-gray-600">
                      Vencimento: {formatDate(conta.vencimento)}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-yellow-600">
                    {formatCurrency(conta.valor)}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  )

  const renderReceitas = () => (
    <div className="space-y-6">
      {/* Filtros e Ações */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por cliente..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
              />
            </div>
          </div>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="vencido">Vencido</option>
            <option value="recebido">Recebido</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <button
            onClick={() => exportarRelatorio('Receitas')}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      {/* Lista de Contas a Receber */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vencimento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Origem
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contasReceber
                .filter(conta => {
                  const matchBusca = busca === '' || conta.cliente.toLowerCase().includes(busca.toLowerCase())
                  const matchStatus = filtroStatus === '' || conta.status === filtroStatus
                  return matchBusca && matchStatus
                })
                .map((conta) => (
                  <tr key={conta._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{conta.cliente}</div>
                        <div className="text-sm text-gray-500">{conta.descricao}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatCurrency(conta.valor)}
                      </div>
                      {conta.parcela && (
                        <div className="text-xs text-gray-500">
                          {conta.parcela}/{conta.total_parcelas}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(conta.vencimento)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(conta.status)}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(conta.status)}`}>
                          {conta.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {conta.origem}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <select
                        value={conta.status}
                        onChange={(e) => atualizarStatusContaReceber(conta._id, e.target.value as any)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="pendente">Pendente</option>
                        <option value="vencido">Vencido</option>
                        <option value="recebido">Recebido</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderDespesas = () => (
    <div className="space-y-6">
      {/* Filtros e Ações */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por fornecedor..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
              />
            </div>
          </div>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="vencido">Vencido</option>
            <option value="pago">Pago</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <button
            onClick={() => exportarRelatorio('Despesas')}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      {/* Lista de Contas a Pagar */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fornecedor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vencimento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contasPagar
                .filter(conta => {
                  const matchBusca = busca === '' || conta.fornecedor.toLowerCase().includes(busca.toLowerCase())
                  const matchStatus = filtroStatus === '' || conta.status === filtroStatus
                  return matchBusca && matchStatus
                })
                .map((conta) => (
                  <tr key={conta._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{conta.fornecedor}</div>
                        <div className="text-sm text-gray-500">{conta.descricao}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {conta.categoria.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatCurrency(conta.valor)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(conta.vencimento)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(conta.status)}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(conta.status)}`}>
                          {conta.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <select
                        value={conta.status}
                        onChange={(e) => atualizarStatusContaPagar(conta._id, e.target.value as any)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="pendente">Pendente</option>
                        <option value="vencido">Vencido</option>
                        <option value="pago">Pago</option>
                        <option value="cancelado">Cancelado</option>
                      </select>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderFluxoCaixa = () => (
    <div className="space-y-6">
      {/* Resumo do Fluxo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <TrendingUp className="text-green-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Entradas Realizadas</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(
                  fluxoCaixa
                    .filter(item => item.tipo === 'entrada' && item.status === 'realizado')
                    .reduce((total, item) => total + item.valor, 0)
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-lg">
              <TrendingDown className="text-red-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Saídas Realizadas</p>
              <p className="text-2xl font-bold text-red-600">
                {formatCurrency(
                  fluxoCaixa
                    .filter(item => item.tipo === 'saida' && item.status === 'realizado')
                    .reduce((total, item) => total + item.valor, 0)
                )}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <BarChart3 className="text-blue-600" size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Saldo Projetado</p>
              <p className="text-2xl font-bold text-blue-600">
                {formatCurrency(
                  fluxoCaixa
                    .filter(item => item.status === 'projecao')
                    .reduce((total, item) => {
                      return item.tipo === 'entrada' ? total + item.valor : total - item.valor
                    }, 0)
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista do Fluxo de Caixa */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Movimentações</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {fluxoCaixa
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
                .map((movimento) => (
                  <tr key={movimento._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(movimento.data)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        movimento.tipo === 'entrada' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {movimento.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movimento.categoria}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movimento.descricao}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <span className={movimento.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}>
                        {movimento.tipo === 'entrada' ? '+' : '-'}{formatCurrency(movimento.valor)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        movimento.status === 'realizado' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {movimento.status}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderMovimentacaoBancaria = () => (
    <div className="space-y-6">
      {/* Header com Ações */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Movimentação Bancária</h3>
            <p className="text-sm text-gray-600">Controle de entradas e saídas por conta bancária</p>
          </div>
          <button
            onClick={() => setShowMovimentacaoForm(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            <Plus size={16} />
            Nova Movimentação
          </button>
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Buscar por descrição..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="pl-10 pr-4 py-2 border rounded-lg w-full"
              />
            </div>
          </div>
          <select
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
            className="px-4 py-2 border rounded-lg"
          >
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="processando">Processando</option>
            <option value="concluido">Concluído</option>
            <option value="cancelado">Cancelado</option>
          </select>
          <button
            onClick={() => exportarRelatorio('Movimentação Bancária')}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      {/* Resumo das Contas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {contasBancarias.map((conta) => (
          <div key={conta._id} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-lg ${conta.principal ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <Banknote className={conta.principal ? 'text-blue-600' : 'text-gray-600'} size={24} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{conta.banco}</h4>
                <p className="text-sm text-gray-600">
                  {conta.tipo_conta.toUpperCase()} {conta.conta}-{conta.digito}
                </p>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Saldo Atual:</span>
                <span className="text-sm font-semibold text-green-600">
                  {formatCurrency(conta.saldo_atual)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Limite:</span>
                <span className="text-sm font-semibold text-blue-600">
                  {formatCurrency(conta.limite)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Disponível:</span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatCurrency(conta.saldo_atual + conta.limite)}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lista de Movimentações */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Conta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Descrição
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Obra
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Centro de Custo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {movimentacoesBancarias
                .filter(mov => {
                  const matchBusca = busca === '' || mov.descricao.toLowerCase().includes(busca.toLowerCase())
                  const matchStatus = filtroStatus === '' || mov.status === filtroStatus
                  return matchBusca && matchStatus
                })
                .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
                .map((movimento) => (
                  <tr key={movimento._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(movimento.data)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        movimento.tipo === 'entrada' 
                          ? 'bg-green-100 text-green-800' 
                          : movimento.tipo === 'saida'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {movimento.tipo}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movimento.conta_bancaria}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        {movimento.categoria.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movimento.descricao}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movimento.obra_id ? (
                        <div className="flex items-center gap-1">
                          <Building size={14} className="text-gray-400" />
                          <span>{movimento.obra_id}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {movimento.centro_custo ? (
                        <div className="flex items-center gap-1">
                          <Target size={14} className="text-gray-400" />
                          <span>{movimento.centro_custo}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <span className={movimento.tipo === 'entrada' ? 'text-green-600' : 'text-red-600'}>
                        {movimento.tipo === 'entrada' ? '+' : '-'}{formatCurrency(movimento.valor)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(movimento.status)}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(movimento.status)}`}>
                          {movimento.status}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditarMovimentacao(movimento)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleExcluirMovimentacao(movimento._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Excluir"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )

  const renderRelatorios = () => (
    <div className="space-y-6">
      {/* Filtros para Relatórios */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Filtros para Relatórios</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Período
            </label>
            <select
              value={filtroRelatorio.periodo}
              onChange={(e) => setFiltroRelatorio(prev => ({ ...prev, periodo: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="ultimo_mes">Último mês</option>
              <option value="ultimos_3_meses">Últimos 3 meses</option>
              <option value="ultimos_6_meses">Últimos 6 meses</option>
              <option value="ultimo_ano">Último ano</option>
              <option value="personalizado">Personalizado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Obra
            </label>
            <select
              value={filtroRelatorio.obra}
              onChange={(e) => setFiltroRelatorio(prev => ({ ...prev, obra: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Todas as obras</option>
              {obras.map((obra) => (
                <option key={obra._id} value={obra._id}>
                  {obra.nome_projeto}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cliente
            </label>
            <select
              value={filtroRelatorio.cliente}
              onChange={(e) => setFiltroRelatorio(prev => ({ ...prev, cliente: e.target.value }))}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="">Todos os clientes</option>
              {[...new Set(obras.map(obra => obra.cliente))].map((cliente) => (
                <option key={cliente} value={cliente}>
                  {cliente}
                </option>
              ))}
            </select>
          </div>
        </div>

        {filtroRelatorio.periodo === 'personalizado' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Início
              </label>
              <input
                type="date"
                value={filtroRelatorio.dataInicio}
                onChange={(e) => setFiltroRelatorio(prev => ({ ...prev, dataInicio: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Fim
              </label>
              <input
                type="date"
                value={filtroRelatorio.dataFim}
                onChange={(e) => setFiltroRelatorio(prev => ({ ...prev, dataFim: e.target.value }))}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        )}
      </div>

      {/* Opções de Relatórios */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="text-blue-600" size={24} />
            <h3 className="text-lg font-semibold">DRE</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Demonstrativo de Resultado do Exercício com análise completa de receitas, custos e despesas
          </p>
          <button
            onClick={() => exportarRelatorio('DRE')}
            disabled={loadingRelatorio}
            className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loadingRelatorio ? 'Gerando...' : 'Gerar DRE'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="text-green-600" size={24} />
            <h3 className="text-lg font-semibold">Margem por Obra</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Análise de rentabilidade detalhada por projeto com custos realizados e margens
          </p>
          <button
            onClick={() => exportarRelatorio('Margem por Obra')}
            disabled={loadingRelatorio}
            className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            {loadingRelatorio ? 'Gerando...' : 'Gerar Relatório'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <PieChart className="text-purple-600" size={24} />
            <h3 className="text-lg font-semibold">Performance Mensal</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Análise de performance financeira mensal com indicadores de crescimento
          </p>
          <button
            onClick={() => exportarRelatorio('Performance Mensal')}
            disabled={loadingRelatorio}
            className="w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {loadingRelatorio ? 'Gerando...' : 'Gerar Relatório'}
          </button>
        </div>
      </div>

      {/* Resumo dos Relatórios */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold mb-4">Sobre os Relatórios</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <h4 className="font-medium text-blue-600 mb-2">DRE - Demonstrativo de Resultado</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Receitas operacionais e financeiras</li>
              <li>• Custos diretos dos serviços</li>
              <li>• Despesas administrativas e comerciais</li>
              <li>• Resultado bruto, operacional e líquido</li>
              <li>• Margem de lucro e análise de performance</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-green-600 mb-2">Margem por Obra</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Valor do contrato vs custos realizados</li>
              <li>• Margem bruta e líquida por projeto</li>
              <li>• Percentual de execução</li>
              <li>• Status e evolução das obras</li>
              <li>• Resumo executivo consolidado</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-purple-600 mb-2">Performance Mensal</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Evolução mensal de receitas e despesas</li>
              <li>• Taxa de crescimento período a período</li>
              <li>• Número de obras ativas e concluídas</li>
              <li>• Ticket médio por obra</li>
              <li>• Indicadores de performance</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Financeiro</h1>
        <p className="text-gray-600">Gestão financeira integrada com controle de receitas, despesas, fluxo de caixa, movimentação bancária e relatórios gerenciais</p>
      </div>

      {/* Navegação por Abas */}
      <div className="mb-8">
        <nav className="flex space-x-8 border-b border-gray-200">
          {[
            { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
            { key: 'receitas', label: 'Receitas', icon: TrendingUp },
            { key: 'despesas', label: 'Despesas', icon: TrendingDown },
            { key: 'fluxo', label: 'Fluxo de Caixa', icon: DollarSign },
            { key: 'bancario', label: 'Movimentação Bancária', icon: Banknote },
            { key: 'relatorios', label: 'Relatórios', icon: FileText }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setAbaAtiva(key as AbaAtiva)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                abaAtiva === key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon size={16} />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* Conteúdo da Aba Ativa */}
      <div className="min-h-screen">
        {abaAtiva === 'dashboard' && renderDashboard()}
        {abaAtiva === 'receitas' && renderReceitas()}
        {abaAtiva === 'despesas' && renderDespesas()}
        {abaAtiva === 'fluxo' && renderFluxoCaixa()}
        {abaAtiva === 'bancario' && renderMovimentacaoBancaria()}
        {abaAtiva === 'relatorios' && renderRelatorios()}
      </div>

      {/* Modal de Movimentação Bancária */}
      <MovimentacaoBancariaForm
        isOpen={showMovimentacaoForm}
        onClose={() => {
          setShowMovimentacaoForm(false)
          setMovimentacaoEditando(null)
        }}
        onSuccess={handleMovimentacaoSuccess}
        movimentacao={movimentacaoEditando}
      />
    </div>
  )
}

export default Financeiro
